import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Plus, Upload, Pencil, Copy, Trash2, Code } from 'lucide-react';
import { AutoPreview } from './TemplateRenderer';
import { extractVariables } from './templateVariables';
import TemplateApiModal from './TemplateApiModal';
import ConfirmDialog from './ConfirmDialog';
import { api } from '../api';

export default function TemplateList({ userId, onEdit }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [apiTemplate, setApiTemplate] = useState(null);
  const [deleting, setDeleting] = useState(null); // template pending delete confirmation
  const fileInput = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { templates } = await api.listTemplates(userId);
      setTemplates(templates);
      setError('');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const duplicate = async (t) => {
    await api.createTemplate({ name: `${t.name} (copy)`, json: t.json, userId });
    load();
  };

  const remove = async (t) => {
    await api.deleteTemplate(t.id);
    setDeleting(null);
    load();
  };

  const onUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      await api.uploadTemplate(file, userId);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      e.target.value = '';
    }
  };

  return (
    <div>
      <div className="page-head">
        <div className="page-title">
          <h2>Templates</h2>
          <p>Design what your screens show — templates are plain JSON.</p>
        </div>
        <div className="page-actions">
          <button className="btn btn-ghost" onClick={() => fileInput.current.click()}>
            <Upload size={16} /> Upload
          </button>
          <button className="btn btn-primary" onClick={() => onEdit(null)}>
            <Plus size={16} /> New template
          </button>
          <input ref={fileInput} type="file" accept=".json,.html,.htm" onChange={onUpload} style={{ display: 'none' }} />
        </div>
      </div>

      {error && <p className="error-text">{error}</p>}
      {loading && (
        <div className="grid-cards" aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className="card" style={{ overflow: 'hidden' }}>
              <div className="skel" style={{ aspectRatio: '16 / 9', borderRadius: 0 }} />
              <div style={{ padding: 14 }}>
                <div className="skel" style={{ height: 16, width: '55%' }} />
                <div className="skel" style={{ height: 28, width: '80%', marginTop: 12 }} />
              </div>
            </div>
          ))}
        </div>
      )}
      {!loading && templates.length === 0 && (
        <div className="empty">No templates yet. Create or upload one to get started.</div>
      )}

      <div className="grid-cards">
        {templates.map((t) => {
          const vars = extractVariables(t.json);
          return (
            <div key={t.id} className="card" style={{ overflow: 'hidden' }}>
              <div className="thumb"><AutoPreview template={t.json} /></div>
              <div style={{ padding: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <strong style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.name}
                  </strong>
                  <span style={{ color: 'var(--faint)', fontSize: 12 }}>
                    {(t.json?.widgets || []).length} widget{(t.json?.widgets || []).length === 1 ? '' : 's'}
                  </span>
                </div>
                {vars.length > 0 && (
                  <div className="chip-row" style={{ marginTop: 8 }}>
                    {vars.slice(0, 4).map((v) => <span key={v} className="chip chip-var">{v}</span>)}
                    {vars.length > 4 && <span className="chip">+{vars.length - 4} more</span>}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => onEdit(t)}>
                    <Pencil size={16} /> Edit
                  </button>
                  <button className="btn btn-ghost btn-sm btn-icon" title="Use via API" onClick={() => setApiTemplate(t)}>
                    <Code size={16} />
                  </button>
                  <button className="btn btn-ghost btn-sm btn-icon" title="Duplicate" onClick={() => duplicate(t)}>
                    <Copy size={16} />
                  </button>
                  <button className="btn btn-danger btn-sm btn-icon" title="Delete" onClick={() => setDeleting(t)}
                    style={{ marginLeft: 'auto' }}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {apiTemplate && <TemplateApiModal template={apiTemplate} onClose={() => setApiTemplate(null)} />}

      <ConfirmDialog
        open={!!deleting}
        danger
        title={`Delete “${deleting?.name}”?`}
        body="Screens currently assigned this template keep showing it until you push something else, but it can't be edited or pushed again."
        confirmLabel="Delete"
        onConfirm={() => remove(deleting)}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
}
